import { useState, useEffect, useMemo, useCallback } from 'react';
import { useOutletContext } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { Undo2, Plus, Check, Ban, Eye, FileText, Building2, User } from 'lucide-react';
import { toast } from 'sonner';
import { useArticleUnits } from '../hooks/useArticleUnits';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { fmtDateTime } from '../lib/datetime';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function userCan(user, module, action) {
  if (!user) return false;
  if (user.role === 'admin') return true;
  return user.permissions?.[module]?.[action] === true;
}

const COND = {
  buen_estado: { label: 'Buen estado (reintegra al stock)', cls: 'bg-green-100 text-green-800' },
  merma_vencido: { label: 'Merma por vencimiento', cls: 'bg-amber-100 text-amber-800' },
  merma_danado: { label: 'Merma por daño', cls: 'bg-red-100 text-red-800' },
};

const STATUS_CLS = {
  pending: 'bg-blue-100 text-blue-800',
  approved: 'bg-green-100 text-green-800',
  rejected: 'bg-red-100 text-red-800',
};

const DEST_LABEL = {
  warehouse: { text: 'Depósito General', cls: 'bg-slate-100 text-slate-800', icon: Building2 },
  family: { text: 'Familiar / Residente', cls: 'bg-purple-100 text-purple-800', icon: User },
};

const emptyFamily = {
  resident_name: '',
  recipient_name: '',
  recipient_dni: '',
  relationship: '',
  observations: '',
};

function InternalReturns() {
  const { user } = useOutletContext();
  const { unitOf } = useArticleUnits();
  const [returns, setReturns] = useState([]);
  const [areas, setAreas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const [confirmApprove, setConfirmApprove] = useState(null);
  const [approving, setApproving] = useState(false);

  // Nuevo formulario
  const [selectedAreaId, setSelectedAreaId] = useState('');
  const [destination, setDestination] = useState('warehouse');
  const [areaStock, setAreaStock] = useState([]);
  const [loadingStock, setLoadingStock] = useState(false);
  const [items, setItems] = useState({}); // key=lot_id → {article_id, article_name, lot_number, exp, max, qty, condition, rem_patient_name}
  const [reason, setReason] = useState('');
  const [family, setFamily] = useState(emptyFamily);
  const [submitting, setSubmitting] = useState(false);

  const canCreate = userCan(user, 'pedidos_internos', 'devolver');
  const canApprove = userCan(user, 'pedidos_internos', 'aprobar');

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [r, a] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/internal-returns`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/internal-areas`, { withCredentials: true }).catch(() => ({ data: [] })),
      ]);
      setReturns(r.data || []);
      setAreas(a.data || []);
    } catch (e) {
      toast.error('Error al cargar devoluciones');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const myAreas = useMemo(() => {
    if (!areas) return [];
    if (user?.role === 'admin' || user?.role === 'supervisor') return areas;
    return areas.filter(a => (a.member_user_ids || []).includes(user?.user_id));
  }, [areas, user]);

  const loadAreaStock = useCallback(async (areaId) => {
    if (!areaId) { setAreaStock([]); return; }
    setLoadingStock(true);
    try {
      const r = await axios.get(`${BACKEND_URL}/api/internal-areas/${areaId}/stock`, { withCredentials: true });
      setAreaStock(r.data?.items || []);
    } catch (e) {
      toast.error('Error al cargar stock del área');
      setAreaStock([]);
    } finally {
      setLoadingStock(false);
    }
  }, []);

  const openNew = () => {
    setSelectedAreaId('');
    setDestination('warehouse');
    setAreaStock([]);
    setItems({});
    setReason('');
    setFamily(emptyFamily);
    setDialogOpen(true);
  };

  const onAreaChange = (areaId) => {
    setSelectedAreaId(areaId);
    setItems({});
    loadAreaStock(areaId);
  };

  const toggleLot = (article, lot) => {
    const key = lot.lot_id;
    setItems(prev => {
      const next = { ...prev };
      if (next[key]) {
        delete next[key];
      } else {
        next[key] = {
          article_id: article.article_id,
          article_name: article.article_name,
          lot_id: lot.lot_id,
          lot_number: lot.lot_number,
          expiration_date: lot.expiration_date,
          rem_patient_name: lot.rem_patient_name,
          origin_area_name: lot.origin_area_name,
          max_qty: lot.quantity_remaining,
          quantity_returned: lot.quantity_remaining,
          condition: 'buen_estado',
        };
      }
      return next;
    });
  };

  const updateItem = (key, patch) => {
    setItems(prev => ({ ...prev, [key]: { ...prev[key], ...patch } }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!selectedAreaId) { toast.error('Elegí un área'); return; }
    const itemsList = Object.values(items).filter(i => i.quantity_returned > 0);
    if (itemsList.length === 0) { toast.error('Seleccioná al menos un lote y cantidad > 0'); return; }
    if (!reason.trim()) { toast.error('Motivo obligatorio'); return; }
    if (destination === 'family') {
      if (!family.resident_name.trim()) { toast.error('Residente obligatorio'); return; }
      if (!family.recipient_name.trim()) { toast.error('Familiar que retira obligatorio'); return; }
      if (!family.recipient_dni.trim()) { toast.error('DNI del familiar obligatorio'); return; }
    }
    setSubmitting(true);
    try {
      const body = {
        area_id: selectedAreaId,
        destination,
        items: itemsList.map(i => ({
          article_id: i.article_id,
          lot_id: i.lot_id,
          quantity_returned: parseInt(i.quantity_returned),
          ...(destination === 'warehouse' ? { condition: i.condition } : {}),
        })),
        reason,
        ...(destination === 'family' ? { family_recipient: family } : {}),
      };
      await axios.post(`${BACKEND_URL}/api/internal-returns`, body, { withCredentials: true });
      toast.success('Devolución registrada');
      setDialogOpen(false);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error');
    } finally {
      setSubmitting(false);
    }
  };

  const executeApprove = async () => {
    if (!confirmApprove) return;
    setApproving(true);
    try {
      await axios.put(`${BACKEND_URL}/api/internal-returns/${confirmApprove.return_id}/approve`, {}, { withCredentials: true });
      toast.success('Devolución aprobada');
      setConfirmApprove(null);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error');
    } finally {
      setApproving(false);
    }
  };

  const reject = async (r) => {
    const reasonTxt = prompt('Motivo de rechazo:');
    if (!reasonTxt) return;
    try {
      await axios.put(`${BACKEND_URL}/api/internal-returns/${r.return_id}/reject`, { reason: reasonTxt }, { withCredentials: true });
      toast.success('Devolución rechazada');
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error');
    }
  };

  const downloadPdf = async (r) => {
    try {
      const resp = await axios.get(`${BACKEND_URL}/api/internal-returns/${r.return_id}/pdf`, { withCredentials: true, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([resp.data], { type: 'application/pdf' }));
      window.open(url, '_blank');
    } catch (err) {
      toast.error('Error al generar PDF');
    }
  };

  const selectedCount = Object.keys(items).length;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold flex items-center gap-2">
          <Undo2 className="w-7 h-7 text-rose-500" /> Devoluciones del Área
        </h1>
        {canCreate && (
          <Button onClick={openNew} data-testid="new-nci-btn">
            <Plus className="w-4 h-4 mr-2" /> Nueva Devolución
          </Button>
        )}
      </div>

      <Card>
        <CardHeader><CardTitle>Listado</CardTitle></CardHeader>
        <CardContent>
          {loading ? <p>Cargando...</p> : returns.length === 0 ? <p className="text-muted-foreground">Sin devoluciones registradas.</p> : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Nº</TableHead>
                  <TableHead>Fecha</TableHead>
                  <TableHead>Área</TableHead>
                  <TableHead>Destino</TableHead>
                  <TableHead>Solicitó</TableHead>
                  <TableHead className="text-center">Items</TableHead>
                  <TableHead>Estado</TableHead>
                  <TableHead className="text-right">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {returns.map(r => {
                  const dest = DEST_LABEL[r.destination || 'warehouse'];
                  const DestIcon = dest.icon;
                  return (
                    <TableRow key={r.return_id} data-testid={`nci-row-${r.return_number}`}>
                      <TableCell className="font-mono text-xs">{r.return_number}</TableCell>
                      <TableCell className="text-xs">{r.created_at && fmtDateTime(r.created_at)}</TableCell>
                      <TableCell>{r.area_name}</TableCell>
                      <TableCell>
                        <Badge className={`${dest.cls} gap-1`}><DestIcon className="w-3 h-3" />{dest.text}</Badge>
                      </TableCell>
                      <TableCell className="text-sm">{r.created_by_name}</TableCell>
                      <TableCell className="text-center">{(r.items || []).length}</TableCell>
                      <TableCell><Badge className={STATUS_CLS[r.status] || 'bg-slate-100'}>{r.status}</Badge></TableCell>
                      <TableCell className="text-right">
                        <Button variant="ghost" size="icon" onClick={() => setDetail(r)} title="Ver detalle" data-testid={`view-nci-${r.return_id}`}>
                          <Eye className="w-4 h-4" />
                        </Button>
                        {r.status === 'approved' && (
                          <Button variant="ghost" size="icon" onClick={() => downloadPdf(r)} title="Descargar PDF" data-testid={`pdf-nci-${r.return_id}`}>
                            <FileText className="w-4 h-4 text-sky-600" />
                          </Button>
                        )}
                        {r.status === 'pending' && canApprove && (
                          <>
                            <Button variant="ghost" size="icon" onClick={() => setConfirmApprove(r)} title="Aprobar" data-testid={`approve-nci-${r.return_id}`}>
                              <Check className="w-4 h-4 text-green-600" />
                            </Button>
                            <Button variant="ghost" size="icon" onClick={() => reject(r)} title="Rechazar" data-testid={`reject-nci-${r.return_id}`}>
                              <Ban className="w-4 h-4 text-red-600" />
                            </Button>
                          </>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Crear */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Nueva Devolución del Área</DialogTitle>
            <DialogDescription>Elegí área, destino y los lotes del stock actual del área a descargar.</DialogDescription>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Área *</Label>
                <select
                  value={selectedAreaId}
                  onChange={(e) => onAreaChange(e.target.value)}
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  required
                  data-testid="nci-area-select"
                >
                  <option value="">Seleccioná un área...</option>
                  {myAreas.map(a => <option key={a.area_id} value={a.area_id}>{a.name}</option>)}
                </select>
              </div>
              <div>
                <Label>Destino *</Label>
                <select
                  value={destination}
                  onChange={(e) => setDestination(e.target.value)}
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  data-testid="nci-destination-select"
                >
                  <option value="warehouse">🏥 Depósito General Interno</option>
                  <option value="family">👨‍👩‍👧 Familiar / Residente</option>
                </select>
              </div>
            </div>

            {destination === 'family' && (
              <Card className="bg-purple-50/40 border-purple-200">
                <CardHeader className="pb-2"><CardTitle className="text-sm">Datos de quien retira</CardTitle></CardHeader>
                <CardContent className="space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs">Residente *</Label>
                      <Input value={family.resident_name} onChange={(e) => setFamily({ ...family, resident_name: e.target.value })} required data-testid="nci-family-resident" />
                    </div>
                    <div>
                      <Label className="text-xs">Vínculo</Label>
                      <Input value={family.relationship} onChange={(e) => setFamily({ ...family, relationship: e.target.value })} placeholder="Hijo/a, esposo/a, sobrino/a..." data-testid="nci-family-rel" />
                    </div>
                    <div>
                      <Label className="text-xs">Familiar que retira *</Label>
                      <Input value={family.recipient_name} onChange={(e) => setFamily({ ...family, recipient_name: e.target.value })} required data-testid="nci-family-name" />
                    </div>
                    <div>
                      <Label className="text-xs">DNI *</Label>
                      <Input value={family.recipient_dni} onChange={(e) => setFamily({ ...family, recipient_dni: e.target.value })} required data-testid="nci-family-dni" />
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs">Observaciones</Label>
                    <Textarea rows={2} value={family.observations} onChange={(e) => setFamily({ ...family, observations: e.target.value })} data-testid="nci-family-obs" />
                  </div>
                </CardContent>
              </Card>
            )}

            <div>
              <Label>Lotes del área {selectedCount > 0 && <span className="text-xs text-muted-foreground">({selectedCount} seleccionado{selectedCount !== 1 ? 's' : ''})</span>}</Label>
              {!selectedAreaId ? (
                <p className="text-sm text-muted-foreground py-4">Elegí un área para ver su stock.</p>
              ) : loadingStock ? (
                <p className="text-sm py-4">Cargando stock...</p>
              ) : areaStock.length === 0 ? (
                <p className="text-sm text-muted-foreground py-4">El área no tiene stock disponible.</p>
              ) : (
                <div className="border rounded-md max-h-[40vh] overflow-y-auto">
                  <Table>
                    <TableHeader className="sticky top-0 bg-background z-10">
                      <TableRow>
                        <TableHead className="w-8"></TableHead>
                        <TableHead>Artículo / Lote</TableHead>
                        <TableHead>Origen</TableHead>
                        <TableHead className="text-center">Disponible</TableHead>
                        <TableHead className="text-center w-24">A devolver</TableHead>
                        {destination === 'warehouse' && <TableHead>Condición</TableHead>}
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {areaStock.flatMap(art => art.lots.map(lot => {
                        const key = lot.lot_id;
                        const selected = !!items[key];
                        const it = items[key];
                        return (
                          <TableRow key={key} className={selected ? 'bg-rose-50/40' : ''}>
                            <TableCell>
                              <input
                                type="checkbox"
                                checked={selected}
                                onChange={() => toggleLot(art, lot)}
                                data-testid={`nci-lot-${lot.lot_id}`}
                              />
                            </TableCell>
                            <TableCell>
                              <div className="font-medium text-sm">{art.article_name}</div>
                              <div className="text-xs text-muted-foreground font-mono">Lote {lot.lot_number || 's/lote'} · Vto {lot.expiration_date ? lot.expiration_date.slice(0,10).split('-').reverse().join('/') : '—'}</div>
                            </TableCell>
                            <TableCell className="text-xs">
                              {lot.rem_patient_name ? <Badge className="bg-pink-100 text-pink-800">REM · {lot.rem_patient_name}</Badge>
                                : lot.origin_area_name ? <Badge className="bg-cyan-100 text-cyan-800">Área · {lot.origin_area_name}</Badge>
                                : <span className="text-muted-foreground">Depósito</span>}
                            </TableCell>
                            <TableCell className="text-center">{lot.quantity_remaining}</TableCell>
                            <TableCell className="text-center">
                              {selected ? (
                                <Input
                                  type="number"
                                  min="1"
                                  max={lot.quantity_remaining}
                                  value={it.quantity_returned}
                                  onChange={(e) => updateItem(key, { quantity_returned: parseInt(e.target.value) || 0 })}
                                  className="w-20 text-center mx-auto"
                                  data-testid={`nci-qty-${lot.lot_id}`}
                                />
                              ) : <span className="text-muted-foreground">—</span>}
                            </TableCell>
                            {destination === 'warehouse' && (
                              <TableCell>
                                {selected ? (
                                  <select
                                    value={it.condition}
                                    onChange={(e) => updateItem(key, { condition: e.target.value })}
                                    className="flex h-9 w-full rounded-md border border-input bg-background px-2 text-xs"
                                    data-testid={`nci-cond-${lot.lot_id}`}
                                  >
                                    {Object.entries(COND).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                                  </select>
                                ) : <span className="text-muted-foreground text-xs">—</span>}
                              </TableCell>
                            )}
                          </TableRow>
                        );
                      }))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </div>

            <div>
              <Label>Motivo *</Label>
              <Textarea rows={2} value={reason} onChange={(e) => setReason(e.target.value)} required data-testid="nci-reason" />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
              <Button type="submit" disabled={submitting || selectedCount === 0} data-testid="nci-submit">
                {submitting ? 'Registrando...' : 'Registrar Devolución'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Detalle */}
      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-2xl">
          {detail && (
            <>
              <DialogHeader>
                <DialogTitle>{detail.return_number}</DialogTitle>
                <DialogDescription>
                  {detail.area_name} · {DEST_LABEL[detail.destination || 'warehouse'].text}
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-3 text-sm">
                <p><b>Fecha:</b> {detail.created_at && fmtDateTime(detail.created_at)}</p>
                <p><b>Motivo:</b> {detail.reason}</p>
                <p><b>Solicitó:</b> {detail.created_by_name}</p>
                {detail.approved_by_name && <p><b>Aprobó:</b> {detail.approved_by_name} ({detail.approved_at && fmtDateTime(detail.approved_at)})</p>}
                {detail.rejection_reason && <p><b>Rechazo:</b> {detail.rejection_reason}</p>}

                {detail.destination === 'family' && detail.family_recipient && (
                  <Card className="bg-purple-50/40 border-purple-200">
                    <CardContent className="pt-4 grid grid-cols-2 gap-2 text-xs">
                      <p><b>Residente:</b> {detail.family_recipient.resident_name}</p>
                      <p><b>Vínculo:</b> {detail.family_recipient.relationship || '—'}</p>
                      <p><b>Retira:</b> {detail.family_recipient.recipient_name}</p>
                      <p><b>DNI:</b> {detail.family_recipient.recipient_dni}</p>
                      {detail.family_recipient.observations && <p className="col-span-2"><b>Observaciones:</b> {detail.family_recipient.observations}</p>}
                    </CardContent>
                  </Card>
                )}

                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Artículo</TableHead>
                      <TableHead>Lote</TableHead>
                      <TableHead className="text-center">Cant.</TableHead>
                      {detail.destination !== 'family' && <TableHead>Condición</TableHead>}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(detail.items || []).map((it, i) => (
                      <TableRow key={i}>
                        <TableCell>{it.article_name || it.article_id} <span className="text-xs text-muted-foreground">· {unitOf(it.article_id)}</span></TableCell>
                        <TableCell className="font-mono text-xs">{it.lot_number || 's/lote'}</TableCell>
                        <TableCell className="text-center">{it.quantity_returned}</TableCell>
                        {detail.destination !== 'family' && (
                          <TableCell>{it.condition ? <Badge className={COND[it.condition]?.cls}>{COND[it.condition]?.label}</Badge> : '—'}</TableCell>
                        )}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>

                {detail.status === 'approved' && (
                  <Button variant="outline" onClick={() => downloadPdf(detail)} className="w-full" data-testid="detail-pdf-btn">
                    <FileText className="w-4 h-4 mr-2" /> Descargar PDF
                  </Button>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={!!confirmApprove}
        onOpenChange={(open) => !open && setConfirmApprove(null)}
        title="Aprobar devolución"
        description={<>¿Aprobar <strong>{confirmApprove?.return_number}</strong>? El stock saldrá del área. {confirmApprove?.destination === 'family' ? 'Se generará el remito de entrega al familiar.' : 'Los lotes en buen estado se reintegran al depósito general; los de merma quedan en cuarentena.'}</>}
        confirmText="Aprobar"
        destructive={false}
        loading={approving}
        onConfirm={executeApprove}
        testId="confirm-approve-nci"
      />
    </div>
  );
}

export default InternalReturns;
